import { useCallback, useMemo, useState } from "react"

import {
  transport,
  useTransportMutation,
  useTransportQueryUtils,
} from "@/transport"
import { useOrganization, useUser } from "@clerk/nextjs"
import {
  ListTable,
  ListTableBody,
  ListTableHead,
  ListTableHeader,
  ListTableRow,
  toast,
} from "@coval/ui"

import { RowNameEditor } from "@/components/tables/RowNameEditor"
import type { WorkspaceResource } from "@/types_api/backend/transport/list_workspaces_response"
import { hasCovalAdminAccess } from "@/utils/auth/coval-admin-access"
import { isSameResourceName } from "@/utils/validation/resourceName"

import { WorkspaceActionDialog } from "../components/WorkspaceActionDialog"
import { WorkspaceRow } from "../components/WorkspaceRow"
import { useWorkspaceContext } from "../contexts/WorkspaceContext"

function useIsWorkspaceAdmin(): boolean {
  const { membership } = useOrganization()
  const { user } = useUser()
  if (membership?.role === "org:admin") {
    return true
  }
  return hasCovalAdminAccess(user?.primaryEmailAddress)
}

function sortWorkspaces(
  workspaces: readonly WorkspaceResource[],
): WorkspaceResource[] {
  const workspaceTypeRank: Record<string, number> = {
    DEFAULT: 0,
    CUSTOM: 1,
  }
  const statusRank: Record<string, number> = {
    ACTIVE: 0,
    ARCHIVED: 1,
  }
  return [...workspaces].sort((a, b) => {
    const aTypeRank = workspaceTypeRank[a.workspace_type] ?? 2
    const bTypeRank = workspaceTypeRank[b.workspace_type] ?? 2
    if (aTypeRank !== bTypeRank) {
      return aTypeRank - bTypeRank
    }
    const aStatusRank = statusRank[a.status] ?? 2
    const bStatusRank = statusRank[b.status] ?? 2
    if (aStatusRank !== bStatusRank) {
      return aStatusRank - bStatusRank
    }
    const nameCmp = a.display_name.localeCompare(b.display_name)
    if (nameCmp !== 0) {
      return nameCmp
    }
    return a.id.localeCompare(b.id)
  })
}

interface WorkspaceSettingsRouteProps {
  getWorkspaceSettingsHref?: (workspace: WorkspaceResource) => string
}

// Matches the workspace create form, src/modules/workspaces/components/WorkspaceCreateForm.tsx.
const WORKSPACE_NAME_MAX_LENGTH = 200

export function WorkspaceSettingsRoute({
  getWorkspaceSettingsHref,
}: WorkspaceSettingsRouteProps = {}) {
  const { workspaces } = useWorkspaceContext()
  const isAdmin = useIsWorkspaceAdmin()
  const queryUtils = useTransportQueryUtils()

  const updateMutation = useTransportMutation(
    transport.operations.updateWorkspace,
  )
  const archiveMutation = useTransportMutation(
    transport.operations.archiveWorkspace,
  )
  const deleteMutation = useTransportMutation(
    transport.operations.deleteWorkspace,
  )

  const [renamingId, setRenamingId] = useState<string | null>(null)

  const [archiveOpen, setArchiveOpen] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState<WorkspaceResource | null>(
    null,
  )

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<WorkspaceResource | null>(
    null,
  )

  const sortedWorkspaces = useMemo(
    () => sortWorkspaces(workspaces),
    [workspaces],
  )

  // Rejects on failure so the row's name field stays open and shows why.
  const saveRename = useCallback(
    async (workspaceItem: WorkspaceResource, displayName: string) => {
      await updateMutation.mutateAsync({
        workspace_id: workspaceItem.id,
        display_name: displayName,
      })
      await queryUtils.invalidateOperation(transport.operations.listWorkspaces)
      toast.success({ title: "Workspace renamed" })
    },
    [updateMutation, queryUtils],
  )

  const handleArchive = useCallback(async () => {
    if (!archiveTarget) {
      return
    }

    try {
      await archiveMutation.mutateAsync({
        workspace_id: archiveTarget.id,
      })
      await queryUtils.invalidateOperation(transport.operations.listWorkspaces)
      setArchiveOpen(false)
      setArchiveTarget(null)
      toast.success({ title: "Workspace archived" })
    } catch (error) {
      toast.error({
        title: "Could not archive workspace",
        error,
        fallback: "Could not archive the workspace.",
      })
    }
  }, [archiveTarget, archiveMutation, queryUtils])

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) {
      return
    }

    try {
      await deleteMutation.mutateAsync({
        workspace_id: deleteTarget.id,
      })
      await queryUtils.invalidateOperation(transport.operations.listWorkspaces)
      setDeleteOpen(false)
      setDeleteTarget(null)
      toast.success({ title: "Workspace deleted" })
    } catch (error) {
      toast.error({
        title: "Could not delete workspace",
        error,
        fallback: "Could not delete the workspace.",
      })
    }
  }, [deleteTarget, deleteMutation, queryUtils])

  const openRename = useCallback((workspaceItem: WorkspaceResource) => {
    setRenamingId(workspaceItem.id)
  }, [])

  const openArchive = useCallback((workspaceItem: WorkspaceResource) => {
    setArchiveTarget(workspaceItem)
    setArchiveOpen(true)
  }, [])

  const openDelete = useCallback((workspaceItem: WorkspaceResource) => {
    setDeleteTarget(workspaceItem)
    setDeleteOpen(true)
  }, [])

  return (
    <div className="flex flex-col gap-4">
      <ListTable>
        <ListTableHeader>
          <ListTableRow>
            <ListTableHead>Workspace</ListTableHead>
            <ListTableHead className="w-72">ID</ListTableHead>
            <ListTableHead className="w-24">Type</ListTableHead>
            <ListTableHead className="w-24">Status</ListTableHead>
            <ListTableHead className="w-12" srOnly>
              Actions
            </ListTableHead>
          </ListTableRow>
        </ListTableHeader>
        <ListTableBody>
          {sortedWorkspaces.map((workspace) => (
            <WorkspaceRow
              key={workspace.id}
              workspace={workspace}
              isAdmin={isAdmin}
              settingsHref={getWorkspaceSettingsHref?.(workspace)}
              onRename={openRename}
              canRename={updateMutation.isReady}
              nameEditor={
                renamingId === workspace.id ? (
                  <RowNameEditor
                    label="Workspace name"
                    resource="workspace"
                    value={workspace.display_name}
                    maxLength={WORKSPACE_NAME_MAX_LENGTH}
                    isNameTaken={(name) =>
                      workspaces.some(
                        (other) =>
                          other.id !== workspace.id &&
                          isSameResourceName(other.display_name, name),
                      )
                    }
                    onSave={(name) => saveRename(workspace, name)}
                    onClose={() => setRenamingId(null)}
                  />
                ) : undefined
              }
              onArchive={openArchive}
              onDelete={openDelete}
            />
          ))}
        </ListTableBody>
      </ListTable>

      {!isAdmin && (
        <p className="text-sm text-coval-text-muted">
          Only organization admins can manage workspaces.
        </p>
      )}

      <WorkspaceActionDialog
        open={archiveOpen}
        title="Archive workspace?"
        description={
          <>
            Archiving <strong>{archiveTarget?.display_name}</strong> will make
            the workspace and its content inaccessible.
          </>
        }
        confirmLabel="Archive"
        pendingLabel="Archiving..."
        isPending={archiveMutation.isPending}
        isReady={archiveMutation.isReady}
        testId="workspace-archive-confirm"
        onOpenChange={(open) => {
          if (!open && !archiveMutation.isPending) {
            setArchiveOpen(false)
          }
        }}
        onConfirm={() => void handleArchive()}
      />

      <WorkspaceActionDialog
        open={deleteOpen}
        title="Delete workspace?"
        description={
          <>
            Deleting <strong>{deleteTarget?.display_name}</strong> will remove
            access to the workspace and all its content. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
        isReady={deleteMutation.isReady}
        testId="workspace-delete-confirm"
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) {
            setDeleteOpen(false)
          }
        }}
        onConfirm={() => void handleDelete()}
      />
    </div>
  )
}
